import { useEffect, useRef, useState } from 'react'
import { Alert, Button } from 'antd'
import { PlatformType } from '@/types/authorization'
import { checkPlatformTokens } from '@/features/auth/api'
import { AUTH_PRIMARY_BUTTON_CLASS } from '../../constants'
import { getProductLabel } from '../utils'
import { constructSignupAuthUrl } from '../utils'
import { useSignup } from '../SignupContext'
import ManualPlatformConnectModal from './ManualPlatformConnectModal'

const SIGNUP_CONNECTION_TOAST_STORAGE_KEY = 'di.signup.connectionToast'

export default function ImportDataStep() {
  const [manualModalOpen, setManualModalOpen] = useState(false)
  const popupCloseIntervalRef = useRef<number | null>(null)
  const {
    customerForm,
    productSelection,
    platformId,
    isConnectingData,
    setIsConnectingData,
    loading,
    setError,
    setInfo,
    finalizeSignup,
    popupRef,
    isBasSignup,
  } = useSignup()

  const productLabel = getProductLabel(productSelection?.product, isBasSignup)

  const queueConnectionSuccessToast = () => {
    sessionStorage.setItem(
      SIGNUP_CONNECTION_TOAST_STORAGE_KEY,
      JSON.stringify({ message: `${productLabel} connected successfully` })
    )
  }

  const clearPopupCloseInterval = () => {
    if (popupCloseIntervalRef.current) {
      window.clearInterval(popupCloseIntervalRef.current)
      popupCloseIntervalRef.current = null
    }
  }

  const resetPopupState = () => {
    clearPopupCloseInterval()
    setIsConnectingData(false)
    popupRef.current = null
  }

  const finalizeNonBrConnection = async () => {
    if (!platformId) {
      resetPopupState()
      return
    }

    try {
      const hasTokens = await checkPlatformTokens(platformId)
      if (!hasTokens) return
      setInfo('Data connection completed successfully.')
      queueConnectionSuccessToast()
      await finalizeSignup()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to complete data connection')
    } finally {
      resetPopupState()
    }
  }

  useEffect(
    () => () => {
      clearPopupCloseInterval()
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close()
      }
      popupRef.current = null
    },
    [popupRef]
  )

  const handleConnectData = () => {
    if (!productSelection?.product || !customerForm?.clientId || !customerForm?.userId) return

    if (productSelection.product === PlatformType.FYI || productSelection.product === PlatformType.Karbon) {
      setManualModalOpen(true)
      return
    }

    const url = constructSignupAuthUrl(
      productSelection.product,
      platformId,
      customerForm.clientId,
      customerForm.userId
    )
    if (url) {
      setIsConnectingData(true)
      popupRef.current = window.open(url, 'br-auth', 'width=600,height=700')
      clearPopupCloseInterval()
      popupCloseIntervalRef.current = window.setInterval(() => {
        if (popupRef.current && !popupRef.current.closed) return
        if (productSelection.product === PlatformType.BRWFM) {
          clearPopupCloseInterval()
          popupRef.current = null
          return
        }
        resetPopupState()
        void finalizeNonBrConnection()
      }, 1000)
      if (!popupRef.current) {
        resetPopupState()
      }
    }
  }

  const handleFinish = async () => {
    await finalizeSignup()
  }

  return (
    <div className="space-y-[1rem]">
      <Alert
        type="success"
        showIcon
        message="Account created successfully"
        description={`To complete setup, connect your ${productLabel} data now or skip and connect later from the Platforms page. If you are done with signup, click the Finish button.`}
      />

      <div className="signup-step-actions">
        <Button onClick={() => void handleConnectData()} disabled={isConnectingData}>
          Connect Your Data
        </Button>
        <Button type="primary" loading={loading} className={AUTH_PRIMARY_BUTTON_CLASS} onClick={() => void handleFinish()}>
          Finish
        </Button>
      </div>
      {isConnectingData && (
        <Alert
          type="info"
          showIcon
          message={productSelection?.product === PlatformType.BRWFM ? 'Connecting WorkflowMax' : 'Waiting for data connection'}
          description={
            productSelection?.product === PlatformType.BRWFM
              ? 'Authentication was received. We are saving your WorkflowMax connection and completing signup now.'
              : 'Please finish the connection in the popup window.'
          }
        />
      )}

      {productSelection?.product && (productSelection.product === PlatformType.FYI || productSelection.product === PlatformType.Karbon) && (
        <ManualPlatformConnectModal
          open={manualModalOpen}
          platformId={platformId}
          clientId={customerForm?.clientId || ''}
          companyName={customerForm?.companyName}
          companyShortName={customerForm?.companyshortName}
          platformType={productSelection.product === PlatformType.FYI ? 'FYI' : 'Karbon'}
          onCancel={() => setManualModalOpen(false)}
          onSuccess={async () => {
            setManualModalOpen(false)
            setInfo('Data connection completed successfully.')
            queueConnectionSuccessToast()
            await finalizeSignup()
          }}
        />
      )}
    </div>
  )
}
